import { useState } from 'react';
import { usePACTs } from '@/hooks/usePACTs';
import { useMotivationalMessages } from '@/hooks/useMotivationalMessages';
import CreatePACTModal from '@/components/CreatePACTModal';
import PACTDetailModal from '@/components/PACTDetailModal';
import ConfettiEffect from '@/components/ConfettiEffect';
import MotivationalBanner from '@/components/MotivationalBanner';
import TodaysProgressCard from '@/components/TodaysProgressCard';
import QuickStatsSection from '@/components/QuickStatsSection';
import PACTsList from '@/components/PACTsList';
import MotivationalQuoteSection from '@/components/MotivationalQuoteSection';
import BottomNavigation from '@/components/BottomNavigation';
import { useToast } from '@/hooks/use-toast';
import { PACT } from '@/types/pact';

const Index = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedPACT, setSelectedPACT] = useState<PACT | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [confettiTrigger, setConfettiTrigger] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");

  const { pacts, createPACT, toggleCompletion, toggleSkip, getActivePACTs, getTodaysProgress, getStreak } = usePACTs();
  const { detectRapidActions, getContextualMessage } = useMotivationalMessages();
  const { toast } = useToast();

  const activePacts = getActivePACTs();
  const todaysProgress = getTodaysProgress();
  const today = new Date().toISOString().split('T')[0];

  const pendingPACTs = activePacts.filter(pact => 
    !pact.completedDays.includes(today) && 
    !pact.skippedDays?.includes(today)
  );
  
  const completedTodayPACTs = activePacts.filter(pact => 
    pact.completedDays.includes(today) || 
    pact.skippedDays?.includes(today)
  );

  const handleCreatePACT = (name: string, duration: number, maxSkips: number) => {
    createPACT(name, duration, maxSkips);
    toast({
      title: "PACT Created! 🎯",
      description: `Ready to show up for "${name}" for ${duration} days?`,
    });
  };

  const handleToggleCompletion = (pactId: string) => {
    const pact = activePacts.find(p => p.id === pactId);
    const wasCompleted = pact?.completedDays.includes(today) || false;
    
    toggleCompletion(pactId, today);
    
    if (!wasCompleted) {
      setConfettiTrigger(true);
      toast({
        title: "You showed up! 🌟",
        description: "Another step closer to your goal. Keep going!",
      });
    } else {
      toast({
        title: "No worries! 💙",
        description: "Come back tomorrow and show up again.",
      });
    }
  };

  const handleToggleSkip = (pactId: string) => {
    const pact = activePacts.find(p => p.id === pactId);
    const skipsRemaining = (pact?.maxSkips || 0) - (pact?.skippedDays?.length || 0);
    
    if (skipsRemaining > 0) {
      toggleSkip(pactId, today);
      toast({
        title: "Day Skipped 📅",
        description: `${skipsRemaining - 1} skips remaining.`,
      });
      // Trigger the motivational banner
      handleShowBanner("", 'now-skipped');
    }
  };

  const handleShowBanner = (message: string, actionType: 'now-completed' | 'now-skipped' | 'now-missed') => {
    console.log(`🎯 Index: handleShowBanner called with actionType: ${actionType}`);
    
    const rapidCount = detectRapidActions();
    const contextualMessage = getContextualMessage(actionType, rapidCount);
    
    console.log(`🎯 Index: Generated contextual message: "${contextualMessage}"`);
    setBannerMessage(contextualMessage);
    setShowBanner(true);
  };

  const handleBannerComplete = () => {
    console.log('🎯 Index: Banner completed, resetting state');
    setShowBanner(false);
    setBannerMessage("");
  };

  const handlePACTCardClick = (pact: PACT) => {
    setSelectedPACT(pact);
    setIsDetailModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-blue to-white">
      <div className="container max-w-md mx-auto px-4 py-8 pb-24">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-deep-blue mb-2">Show Up!</h1>
          <p className="text-deep-blue/70">
            {todaysProgress.total === 0 
              ? "Ready to create your first PACT?" 
              : todaysProgress.completed === todaysProgress.total 
                ? "Amazing! You showed up today! 🌟"
                : todaysProgress.completed === 0 
                  ? "Today is a new chance to show up!"
                  : `${todaysProgress.completed} of ${todaysProgress.total} done. Keep going!`
            }
          </p>
        </div>

        <TodaysProgressCard todaysProgress={todaysProgress} />
        <QuickStatsSection activePacts={activePacts} getStreak={getStreak} />
        
        <PACTsList
          pendingPACTs={pendingPACTs}
          completedTodayPACTs={completedTodayPACTs}
          activePacts={activePacts}
          today={today}
          getStreak={getStreak}
          handleToggleCompletion={handleToggleCompletion}
          handleToggleSkip={handleToggleSkip}
          handlePACTCardClick={handlePACTCardClick}
          handleShowBanner={handleShowBanner}
          onCreatePACT={() => setIsCreateModalOpen(true)}
        />

        <MotivationalQuoteSection />
      </div>

      {/* Modals and Effects */}
      <CreatePACTModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreatePACT={handleCreatePACT}
      />

      <PACTDetailModal
        pact={selectedPACT}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        getStreak={getStreak}
        toggleCompletion={toggleCompletion}
        toggleSkip={toggleSkip}
        allPacts={pacts}
      />

      <ConfettiEffect
        trigger={confettiTrigger}
        onComplete={() => setConfettiTrigger(false)}
      />

      <MotivationalBanner
        show={showBanner}
        message={bannerMessage}
        onComplete={handleBannerComplete}
      />

      <BottomNavigation />
    </div>
  );
};

export default Index;
